import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Sparkles, MapPin } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const card = "rounded-2xl bg-white border border-slate-200 p-5";
const pct = (v) => (v == null ? "—" : `${Math.round(v * 1000) / 10}%`);

export default function AddressGroups() {
  const [groups, setGroups] = useState(null);
  const [form, setForm] = useState({ suburb: "", count: 100, min_water_confidence: "", exclude_mailed: true, exclude_leads: true, exclude_customers: true });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/admin/address-groups").then(({ data }) => setGroups(data.groups)).catch(() => {});
  useEffect(() => { load(); }, []);

  const findNext = async () => {
    if (!form.suburb.trim()) return toast.error("Enter an area/suburb");
    setBusy(true); setPreview(null);
    try {
      const { data } = await api.post("/admin/address-groups/find-next", { ...form, count: Number(form.count) || 100 });
      setPreview(data);
      if (data.selected_count === 0) toast.warning("No eligible properties found for that area");
      else if (data.short) toast.warning(`Only ${data.selected_count} of ${data.requested} requested eligible properties available — you decide whether to proceed`);
    } catch (e) { toast.error(e.response?.data?.detail || "Search failed"); }
    finally { setBusy(false); }
  };

  const createNext = async () => {
    if (!window.confirm(
      `Create campaign ${preview.next_code} with ${preview.selected_count} properties?\n\nThe campaign is created as DRAFT. No letters are generated and no mail is sent — you review and send from the campaign page with the usual TEST/LIVE gates.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/address-groups/create-next", {
        confirm: true, prefix: preview.prefix, suburb: form.suburb,
        property_ids: preview.properties.map((p) => p.id),
      });
      toast.success(`Campaign ${data.code} created with ${data.properties_assigned} properties (draft — nothing mailed)`);
      navigate(`/admin/property-campaigns/${data.campaign.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  const openGroup = async (g) => {
    if (g.campaign_id) return navigate(`/admin/property-campaigns/${g.campaign_id}`);
    // Sep 2026 — Admin chooses the mail format when preparing a group.
    // Default is A4 Letter; Postcard must be an explicit choice.
    const answer = window.prompt(
      `${g.code} doesn't have an underlying campaign record yet.\n\n` +
      `Prepare Campaign now? This creates a DRAFT campaign with the group's ` +
      `${g.total_properties} assigned properties and their QR codes. Nothing is ` +
      `generated and nothing is mailed — you review and send from the campaign page.\n\n` +
      `MAIL FORMAT — type 1 or 2:\n  1 = A4 Letter (default)\n  2 = Postcard (210x130mm)\n\n` +
      `Cancel to abort.`, "1");
    if (answer === null) return;
    const mail_format = answer.trim() === "2" ? "postcard" : "a4_letter";
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/address-groups/${encodeURIComponent(g.code)}/prepare`, { confirm: true, mail_format });
      toast.success(data.created
        ? `Draft ${mail_format === "postcard" ? "POSTCARD" : "A4 letter"} campaign ${g.code} prepared with ${data.properties_assigned} properties (nothing mailed)`
        : `Linked to existing campaign ${data.campaign.name}`);
      navigate(`/admin/property-campaigns/${data.campaign.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Prepare failed"); }
    finally { setBusy(false); }
  };

  const sel = "rounded-lg border border-slate-300 text-sm px-2 py-1.5 bg-white";

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="address-groups-page">
      <Link to="/admin/address-database" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Address Database</Link>
      <h1 className="text-2xl font-bold text-slate-900 mt-2">Address Groups</h1>

      <div className={`${card} mt-4`} data-testid="create-next-panel">
        <div className="font-semibold text-slate-900 flex items-center gap-2"><Sparkles className="w-4 h-4 text-cyan-600" /> Create Next Campaign</div>
        <div className="text-xs text-slate-500 mt-1">Pulls the next unmailed properties street-by-street. Always excludes do-not-mail, invalid addresses and unresolved conflicts. Never sends mail.</div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input className={`${sel} w-44`} placeholder="Area / suburb (e.g. Doonan)" value={form.suburb}
            onChange={(e) => setForm({ ...form, suburb: e.target.value })} data-testid="cn-suburb" />
          <input className={`${sel} w-20`} type="number" min="1" max="1000" value={form.count}
            onChange={(e) => setForm({ ...form, count: e.target.value })} data-testid="cn-count" />
          <select className={sel} value={form.min_water_confidence} onChange={(e) => setForm({ ...form, min_water_confidence: e.target.value })}>
            <option value="">Any water confidence</option><option>High</option><option>To Verify</option>
          </select>
          {[["exclude_mailed", "Exclude previously mailed"], ["exclude_leads", "Exclude leads"], ["exclude_customers", "Exclude customers"]].map(([k, l]) => (
            <label key={k} className="text-xs text-slate-600 inline-flex items-center gap-1">
              <input type="checkbox" checked={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} data-testid={`cn-${k}`} /> {l}
            </label>
          ))}
          <Button disabled={busy} onClick={findNext} className="rounded-full bg-cyan-700" data-testid="cn-find-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Find Next ${form.count || 100}`}
          </Button>
        </div>

        {preview && preview.selected_count > 0 && (
          <div className="mt-4 border-t border-slate-200 pt-4" data-testid="cn-preview">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-slate-700">
                <b>{preview.selected_count}</b> of {preview.requested} requested · {preview.eligible_total} eligible in area ·
                next campaign ID <b className="font-mono">{preview.next_code}</b>
                {preview.short && <span className="text-amber-700 font-semibold"> — fewer than requested available</span>}
              </div>
              <Button disabled={busy} onClick={createNext} className="rounded-full bg-emerald-700" data-testid="cn-create-btn">
                Approve & Create {preview.next_code} ({preview.selected_count})
              </Button>
            </div>
            <div className="text-xs text-slate-500 mt-1.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> Streets: {preview.streets.join(", ") || "—"}</div>
            <div className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-slate-200">
              <table className="w-full text-xs">
                <tbody>
                  {preview.properties.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100">
                      <td className="px-3 py-1.5">{p.street_address}, {p.suburb} {p.postcode}</td>
                      <td className="px-3 py-1.5 text-slate-500">{p.water_confidence || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 rounded-2xl bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="groups-table">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
            <th className="px-4 py-2.5">Campaign ID</th><th className="px-2 py-2.5">Name</th><th className="px-2 py-2.5">Suburb</th>
            <th className="px-2 py-2.5">Props</th><th className="px-2 py-2.5">Mailed</th><th className="px-2 py-2.5">Scans</th>
            <th className="px-2 py-2.5">Forms</th><th className="px-2 py-2.5">Leads</th><th className="px-2 py-2.5">Bookings</th>
            <th className="px-2 py-2.5">Insp.</th><th className="px-2 py-2.5">Tests</th><th className="px-2 py-2.5">Conv.</th><th className="px-2 py-2.5">Status</th>
          </tr></thead>
          <tbody>
            {(groups || []).map((g) => (
              <tr key={g.code} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                data-testid={`group-row-${g.code}`}
                onClick={() => openGroup(g)}>
                <td className="px-4 py-2.5">
                  <span className="font-mono font-semibold text-cyan-700 underline underline-offset-2 hover:text-cyan-900" data-testid={`group-open-${g.code}`}>
                    {g.code}
                  </span>
                </td>
                <td className="px-2 py-2.5">{g.campaign_name || <span className="text-slate-400">imported group — click to prepare</span>}</td>
                <td className="px-2 py-2.5 text-slate-600">{g.suburb || "—"}</td>
                <td className="px-2 py-2.5">{g.total_properties}</td>
                <td className="px-2 py-2.5">{g.mailed}</td>
                <td className="px-2 py-2.5">{g.qr_scans}</td>
                <td className="px-2 py-2.5">{g.forms_completed}</td>
                <td className="px-2 py-2.5">{g.leads}</td>
                <td className="px-2 py-2.5">{g.bookings}</td>
                <td className="px-2 py-2.5">{g.inspections}</td>
                <td className="px-2 py-2.5">{g.tests}</td>
                <td className="px-2 py-2.5">{pct(g.conversion_rate)}</td>
                <td className="px-2 py-2.5">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${g.campaign_status === "mailed" ? "bg-amber-200 text-amber-900" : g.campaign_status === "draft" ? "bg-slate-200 text-slate-600" : g.campaign_status ? "bg-cyan-100 text-cyan-800" : "bg-slate-100 text-slate-400"}`}>
                    {g.campaign_status || "not created"}
                  </span>
                </td>
              </tr>
            ))}
            {groups && !groups.length && <tr><td colSpan={13} className="px-4 py-8 text-center text-slate-400">No campaign groups yet — import your master spreadsheet first.</td></tr>}
          </tbody>
        </table>
        {!groups && <div className="p-8 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>}
      </div>
    </div>
  );
}
